import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import api from "../services/api";

const Providers: React.FC = () => {
  const [providers, setProviders] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [balance, setBalance] = useState<any>(null);
  const [, setLoading] = useState(false);
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  const [depositForm, setDepositForm] = useState({
    amount: "",
    balanceType: "working" as "working" | "transit",
    comment: "",
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const data = await api.getProviders();
      setProviders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const fetchProviderBalance = async (providerId: string) => {
    setLoading(true);
    try {
      // Для провайдеров с API интеграцией
      if (selectedProvider?.apiPrefix) {
        const balanceData = await api.getProviderIntegrationBalance();
        setBalance(balanceData);
      } else {
        // Для провайдеров без API — показываем внутренний баланс
        setBalance({ balance: selectedProvider?.balance || 0, currency: "USDT" });
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
      setBalance(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDepositSubmit = async () => {
    if (!selectedProvider) return;

    const amount = parseFloat(depositForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setNotification({
        open: true,
        message: "Введите корректную сумму",
        severity: "error",
      });
      return;
    }

    try {
      const result = await api.testProviderDeposit({
        providerId: selectedProvider.id,
        amount,
        balanceType: depositForm.balanceType,
        comment: depositForm.comment || undefined,
      });

      setNotification({
        open: true,
        message: `Баланс провайдера ${
          result.providerLogin
        } успешно пополнен на ${amount} ${
          depositForm.balanceType === "working" ? "рабочего" : "переходного"
        } баланса`,
        severity: "success",
      });

      // Обновляем список провайдеров
      fetchProviders();

      // Закрываем диалог и сбрасываем форму
      setShowDepositDialog(false);
      setDepositForm({
        amount: "",
        balanceType: "working",
        comment: "",
      });
    } catch (error: any) {
      console.error("Error depositing:", error);
      setNotification({
        open: true,
        message:
          error.response?.data?.message || "Ошибка при пополнении баланса",
        severity: "error",
      });
    }
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "apiPrefix", headerName: "API Prefix", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "ACTIVE" ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 150,
      valueGetter: (params) =>
        `${params.row.balance || 0} ${params.row.balanceCurrency || "RUB"}`,
    },
    {
      field: "commission",
      headerName: "Commission",
      width: 120,
      valueFormatter: (params) => `${params.value || 0}%`,
    },
    { field: "minAmount", headerName: "Min Amount", width: 120 },
    { field: "maxAmount", headerName: "Max Amount", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProvider(params.row);
              fetchProviderBalance(params.row.id);
            }}
          >
            View Details
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProvider(params.row);
              setShowDepositDialog(true);
            }}
          >
            Пополнить
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Provider Management
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Providers
              </Typography>
              <Typography variant="h4">{providers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Providers
              </Typography>
              <Typography variant="h4">
                {providers.filter((p) => p.status === "ACTIVE").length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Balance
              </Typography>
              <Typography variant="h4">
                {providers
                  .reduce((sum, p) => sum + (p.balance || 0), 0)
                  .toFixed(2)}{" "}
                RUB
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Commission
              </Typography>
              <Typography variant="h4">
                {(
                  providers.reduce((sum, p) => sum + (p.commission || 0), 0) /
                    providers.length || 0
                ).toFixed(1)}
                %
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <DataGrid
          rows={providers}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          autoHeight
          onRowClick={(params) => {
            setSelectedProvider(params.row);
            fetchProviderBalance(params.row.id);
          }}
        />
      </Paper>

      <Dialog
        open={!!selectedProvider}
        onClose={() => {
          setSelectedProvider(null);
          setBalance(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Provider Details: {selectedProvider?.name}</DialogTitle>
        <DialogContent>
          {selectedProvider && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="ID"
                        secondary={selectedProvider.id}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Name"
                        secondary={selectedProvider.name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="API Prefix"
                        secondary={selectedProvider.apiPrefix}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Status"
                        secondary={selectedProvider.status}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Commission"
                        secondary={`${selectedProvider.commission || 0}%`}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Min Amount"
                        secondary={selectedProvider.minAmount || "N/A"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Max Amount"
                        secondary={selectedProvider.maxAmount || "N/A"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Balance"
                        secondary={`${selectedProvider.balance || 0} ${
                          selectedProvider.balanceCurrency || "RUB"
                        }`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Auto Withdraw"
                        secondary={
                          selectedProvider.autoWithdraw ? "Enabled" : "Disabled"
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {balance && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Live Balance
                  </Typography>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary">
                        {balance.balance} {balance.currency || "RUB"}
                      </Typography>
                      {balance.details && (
                        <pre>{JSON.stringify(balance.details, null, 2)}</pre>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {selectedProvider.apiConfig && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    API Configuration
                  </Typography>
                  <Card>
                    <CardContent>
                      <pre>
                        {JSON.stringify(selectedProvider.apiConfig, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Диалог пополнения баланса */}
      <Dialog
        open={showDepositDialog}
        onClose={() => {
          setShowDepositDialog(false);
          setDepositForm({
            amount: "",
            balanceType: "working",
            comment: "",
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Пополнить баланс провайдера: {selectedProvider?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Сумма"
              type="number"
              value={depositForm.amount}
              onChange={(e) =>
                setDepositForm({ ...depositForm, amount: e.target.value })
              }
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
            />

            <FormControl fullWidth>
              <InputLabel>Тип баланса</InputLabel>
              <Select
                value={depositForm.balanceType}
                label="Тип баланса"
                onChange={(e) =>
                  setDepositForm({
                    ...depositForm,
                    balanceType: e.target.value as "working" | "transit",
                  })
                }
              >
                <MenuItem value="working">Рабочий баланс</MenuItem>
                <MenuItem value="transit">Переходной баланс</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Комментарий (необязательно)"
              multiline
              rows={3}
              value={depositForm.comment}
              onChange={(e) =>
                setDepositForm({ ...depositForm, comment: e.target.value })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDepositDialog(false);
              setDepositForm({
                amount: "",
                balanceType: "working",
                comment: "",
              });
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleDepositSubmit}
            variant="contained"
            disabled={
              !depositForm.amount || parseFloat(depositForm.amount) <= 0
            }
          >
            Пополнить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Providers;
